
import React, {
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";

import PageLayout from "../../layouts/PageLayout";

import {
  Row,
  Col,
  Form,
  Button,
  Container,
} from "react-bootstrap";

import { TranslatorContext } from "../../context/Translator";

import {
  Link,
  useNavigate,
} from "react-router-dom";

import axios from "axios";

export default function AddProductPage() {
  const { t } = useContext(TranslatorContext);
  const navigate = useNavigate();

  // --------------------------------------------------
  // Loading / Submit
  // --------------------------------------------------

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // --------------------------------------------------
  // API Data
  // --------------------------------------------------

  const [categories, setCategories] = useState([]);
  const [childCategories, setChildCategories] = useState([]);
  const [listers, setListers] = useState([]);
  const [mplacers, setMplacers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [stores, setStores] = useState([]);
  // --------------------------------------------------
  // Category
  // --------------------------------------------------

  const [selectedParentCategory, setSelectedParentCategory] =
    useState("");

  // --------------------------------------------------
  // Form
  // --------------------------------------------------

  const [formData, setFormData] = useState({
    category_id: "",
    lister_id: "",
    mplacer_id: "",
    brand_id: "",
    store_id: "",
    product_condition: "Used",
    selling_style: "Fixed",
    title: "",
    short_buying_price: "",
    buying_price: "",
    sell_price_dollar: "",
    shipping_charges_dollar: "",
  });

  // --------------------------------------------------
  // Image
  // --------------------------------------------------

  const [firstImg, setFirstImg] = useState(null);
  const [imgPreview, setImgPreview] = useState(null);

  // --------------------------------------------------
  // Errors
  // --------------------------------------------------

  const [errors, setErrors] = useState({});

  // --------------------------------------------------
  // Camera
  // --------------------------------------------------

  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);

  const videoRef = useRef(null);

  // ==================================================
  // INITIAL DATA
  // ==================================================

  useEffect(() => {
    fetchInitialData();
  }, []);

  // ==================================================
  // CLEANUP CAMERA
  // ==================================================

  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => {
          track.stop();
        });
      }
    };
  }, [cameraStream]);

  // ==================================================
  // FETCH INITIAL DATA
  // ==================================================

  const fetchInitialData = async () => {
    setLoading(true);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/issuer/products/initial-data`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authToken"
            )}`,
          },
        }
      );

      if (response.data.success) {
        const data = response.data.data;

        // Product categories
        setCategories(
          data.product_categories || []
        );

        // Listers
        setListers(
          data.listers || []
        );
        setMplacers(
          data.mplacers || []
        );
        setBrands(
          data.brands || []
        );
        setStores(
          data.stores || []
        );
      }
    } catch (error) {
      console.error(
        "Error loading initial form data:",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  // ==================================================
  // PARENT CATEGORY CHANGE
  // ==================================================

  const handleParentCategoryChange = (e) => {
    const parentId = e.target.value;

    setSelectedParentCategory(parentId);

    const parentCat = categories.find(
      (cat) =>
        cat.id === parseInt(parentId, 10)
    );

    if (
      parentCat &&
      Array.isArray(parentCat.child_categories)
    ) {
      setChildCategories(
        parentCat.child_categories
      );
    } else {
      setChildCategories([]);
    }

    // Reset child category
    setFormData((prev) => ({
      ...prev,
      category_id: "",
    }));

    // Clear category error
    if (errors.category_id) {
      setErrors((prev) => ({
        ...prev,
        category_id: null,
      }));
    }
  };

  // ==================================================
  // FORM CHANGE
  // ==================================================

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  // ==================================================
  // NORMAL IMAGE UPLOAD
  // ==================================================

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    // Stop camera if open
    stopCamera();

    setFirstImg(file);

    setImgPreview(
      URL.createObjectURL(file)
    );

    if (errors.first_img) {
      setErrors((prev) => ({
        ...prev,
        first_img: null,
      }));
    }
  };

  // ==================================================
  // OPEN CAMERA
  // ==================================================

  const openCamera = async () => {
    try {
      if (
        !navigator.mediaDevices ||
        !navigator.mediaDevices.getUserMedia
      ) {
        alert(
          "Camera is not supported by this browser."
        );

        return;
      }

      // Close previous stream
      stopCamera();

      const stream =
        await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: {
              ideal: "environment",
            },
            width: {
              ideal: 1920,
            },
            height: {
              ideal: 1080,
            },
          },
          audio: false,
        });

      setCameraStream(stream);
      setCameraOpen(true);

      // Wait for modal/video to render
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject =
            stream;

          videoRef.current
            .play()
            .catch((error) => {
              console.error(
                "Video play error:",
                error
              );
            });
        }
      }, 100);
    } catch (error) {
      console.error(
        "Camera error:",
        error
      );

      if (
        error.name ===
        "NotAllowedError"
      ) {
        alert(
          "Camera permission was denied. Please allow camera access."
        );
      } else if (
        error.name ===
        "NotFoundError"
      ) {
        alert(
          "No camera was found on this device."
        );
      } else if (
        error.name ===
        "NotReadableError"
      ) {
        alert(
          "The camera is already being used by another application."
        );
      } else {
        alert(
          "Unable to open the camera."
        );
      }
    }
  };

  // ==================================================
  // STOP CAMERA
  // ==================================================

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream
        .getTracks()
        .forEach((track) => {
          track.stop();
        });
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setCameraStream(null);
    setCameraOpen(false);
  };

  // ==================================================
  // CLOSE CAMERA
  // ==================================================

  const closeCamera = () => {
    stopCamera();
  };

  // ==================================================
  // CAPTURE PHOTO
  // ==================================================

  const capturePhoto = () => {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    if (
      !video.videoWidth ||
      !video.videoHeight
    ) {
      alert(
        "Camera is not ready yet. Please wait a moment."
      );

      return;
    }

    const canvas =
      document.createElement("canvas");

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context =
      canvas.getContext("2d");

    context.drawImage(
      video,
      0,
      0,
      canvas.width,
      canvas.height
    );

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          alert(
            "Unable to capture image."
          );

          return;
        }

        const file = new File(
          [blob],
          `product-${Date.now()}.jpg`,
          {
            type: "image/jpeg",
          }
        );

        setFirstImg(file);

        setImgPreview(
          URL.createObjectURL(file)
        );

        if (errors.first_img) {
          setErrors((prev) => ({
            ...prev,
            first_img: null,
          }));
        }

        // Close camera
        stopCamera();
      },
      "image/jpeg",
      0.9
    );
  };

  // ==================================================
  // REMOVE IMAGE
  // ==================================================

  const removeImage = () => {
    setFirstImg(null);
    setImgPreview(null);
  };

  // ==================================================
  // SUBMIT
  // ==================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSubmitting(true);
    setErrors({});

    const submitData = new FormData();

    // Add form fields
    Object.keys(formData).forEach(
      (key) => {
        if (
          formData[key] !== null &&
          formData[key] !== ""
        ) {
          submitData.append(
            key,
            formData[key]
          );
        }
      }
    );

    // Add main image
    if (firstImg) {
      submitData.append(
        "first_img",
        firstImg
      );
    }

    try {
      const response =
        await axios.post(
          `${process.env.REACT_APP_API_URL}/issuer/products`,
          submitData,
          {
            headers: {
              "Content-Type":
                "multipart/form-data",

              Authorization: `Bearer ${localStorage.getItem(
                "authToken"
              )}`,
            },
          }
        );

      if (response.data.success) {
        navigate("/issue-product-list");
      }
    } catch (error) {
      if (
        error.response &&
        error.response.status === 422
      ) {
        setErrors(
          error.response.data.errors ||
          {}
        );
      } else {
        console.error(
          "Error storing product:",
          error
        );

        alert(
          "Something went wrong while saving the product."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ==================================================
  // LOADING
  // ==================================================

  if (loading) {
    return (
      <PageLayout>
        <div className="text-center p-5">
          <div className="spinner"></div>

          <p>
            Loading Product Setup...
          </p>
        </div>
      </PageLayout>
    );
  }

  // ==================================================
  // UI
  // ==================================================

  return (
    <PageLayout>

      <Container fluid className="px-0">

        {/* =========================================
            BREADCRUMB
        ========================================== */}

        <Row>
          <Col xl={12}>

            <div className="mc-card">

              <div className="mc-breadcrumb">

                <h3 className="mc-breadcrumb-title">
                  {"Add New Product"}
                </h3>

                <ul className="mc-breadcrumb-list">

                  <li className="mc-breadcrumb-item">

                    <Link
                      to="#"
                      className="mc-breadcrumb-link"
                    >
                      {t("home") ||
                        "Home"}
                    </Link>

                  </li>

                  <li className="mc-breadcrumb-item">

                    <Link
                      to="/products"
                      className="mc-breadcrumb-link"
                    >
                      {t("products") ||
                        "Products"}
                    </Link>

                  </li>

                  <li className="mc-breadcrumb-item">

                    {t("add_product") ||
                      "Add Product"}

                  </li>

                </ul>

              </div>

            </div>

          </Col>
        </Row>

        {/* =========================================
            FORM
        ========================================== */}

        <Form onSubmit={handleSubmit}>


          <Row>

            {/* =====================================
                LEFT SIDE
            ====================================== */}

            <Col lg={8}>

              {/* ===================================
                  PRODUCT DETAILS
              ==================================== */}

              <div className="mc-card mb-4">

                <h4 className="card-title mb-4">
                  Product Details
                </h4>
                <Row>
                  <Col md={6}>

                    <Form.Group className="mb-3">

                      <Form.Label>
                        Store{" "}
                        <span className="text-danger">
                          *
                        </span>
                      </Form.Label>

                      <Form.Select
                        name="store_id"
                        value={
                          formData.store_id
                        }
                        onChange={
                          handleInputChange
                        }
                        isInvalid={
                          !!errors.store_id
                        }
                      >

                        <option value="">
                          Select Store
                        </option>

                        {stores.map((store) => (
                          <option key={store.id} value={store.id}>
                            {store.name}
                          </option>
                        ))}

                      </Form.Select>

                      <Form.Control.Feedback type="invalid">
                        {errors.store_id && errors.store_id[0]}
                      </Form.Control.Feedback>

                    </Form.Group>

                  </Col>
                  <Col md={6}>

                    <Form.Group className="mb-3">

                      <Form.Label>
                        Product Condition{" "}
                        <span className="text-danger">
                          *
                        </span>
                      </Form.Label>

                      <Form.Select
                        name="product_condition"
                        value={
                          formData.product_condition
                        }
                        onChange={
                          handleInputChange
                        }
                        isInvalid={
                          !!errors.product_condition
                        }
                      >

                        <option value="used">
                          Used
                        </option>

                        <option value="new">
                          New
                        </option>

                      </Form.Select>

                      <Form.Control.Feedback type="invalid">
                        {errors.product_condition &&
                          errors.product_condition[0]}
                      </Form.Control.Feedback>

                    </Form.Group>

                  </Col>

                  {/* PARENT */}

                  <Col md={6}>

                    <Form.Group className="mb-3">

                      <Form.Label>
                        Parent Category
                      </Form.Label>

                      <Form.Select
                        value={
                          selectedParentCategory
                        }
                        onChange={
                          handleParentCategoryChange
                        }
                      >

                        <option value="">
                          Select ....
                        </option>

                        {categories.map(
                          (cat) => (
                            <option
                              key={cat.id}
                              value={cat.id}
                            >
                              {cat.name}
                            </option>
                          )
                        )}

                      </Form.Select>

                    </Form.Group>

                  </Col>

                  {/* CHILD */}

                  <Col md={6}>

                    <Form.Group className="mb-3">

                      <Form.Label>
                        Sub Category{" "}
                        <span className="text-danger">
                          *
                        </span>
                      </Form.Label>

                      <Form.Select
                        name="category_id"
                        value={
                          formData.category_id
                        }
                        onChange={
                          handleInputChange
                        }
                        isInvalid={
                          !!errors.category_id
                        }
                        disabled={
                          !selectedParentCategory
                        }
                      >

                        <option value="">
                          Select ....
                        </option>

                        {childCategories.map(
                          (child) => (
                            <option
                              key={child.id}
                              value={child.id}
                            >
                              {child.name}
                            </option>
                          )
                        )}

                      </Form.Select>

                      <Form.Control.Feedback type="invalid">
                        {errors.category_id &&
                          errors.category_id[0]}
                      </Form.Control.Feedback>

                    </Form.Group>

                  </Col>

                </Row>
                {/* TITLE */}

                <Form.Group className="mb-3">

                  <Form.Label>
                    Title{" "}
                    <span className="text-danger">
                      *
                    </span>
                  </Form.Label>

                  <Form.Control
                    type="text"
                    name="title"
                    value={
                      formData.title
                    }
                    onChange={
                      handleInputChange
                    }
                    isInvalid={
                      !!errors.title
                    }
                    placeholder="e.g. Rolex Submariner Date 126610LN"
                  />

                  <Form.Control.Feedback type="invalid">
                    {errors.title &&
                      errors.title[0]}
                  </Form.Control.Feedback>

                </Form.Group>

                {/* CATEGORY */}


                {/* SELLING STYLE / LISTER */}

                <Row>
                  {/* SELLING STYLE */}

                  <Col md={6}>

                    <Form.Group className="mb-3">

                      <Form.Label>
                        Brand{" "}
                        <span className="text-danger">
                          *
                        </span>
                      </Form.Label>

                      <Form.Select
                        name="brand_id"
                        value={
                          formData.brand_id
                        }
                        onChange={
                          handleInputChange
                        }
                        isInvalid={
                          !!errors.brand_id
                        }
                      >

                        <option value="">
                          Select Brand
                        </option>

                        {brands.map((brand) => (
                          <option key={brand.id} value={brand.id}>
                            {brand.name}
                          </option>
                        ))}

                      </Form.Select>

                      <Form.Control.Feedback type="invalid">
                        {errors.brand_id && errors.brand_id[0]}
                      </Form.Control.Feedback>

                    </Form.Group>

                  </Col>



                  {/* SELLING STYLE */}

                  <Col md={6}>

                    <Form.Group className="mb-3">

                      <Form.Label>
                        Selling Style{" "}
                        <span className="text-danger">
                          *
                        </span>
                      </Form.Label>

                      <Form.Select
                        name="selling_style"
                        value={
                          formData.selling_style
                        }
                        onChange={
                          handleInputChange
                        }
                        isInvalid={
                          !!errors.selling_style
                        }
                      >

                        <option value="Fixed">
                          Fixed
                        </option>

                        <option value="Auction">
                          Auction
                        </option>

                      </Form.Select>

                      <Form.Control.Feedback type="invalid">
                        {errors.selling_style &&
                          errors.selling_style[0]}
                      </Form.Control.Feedback>

                    </Form.Group>

                  </Col>

                  {/* LISTER */}

                  <Col md={6}>

                    <Form.Group className="mb-3">

                      <Form.Label>
                        Assign Lister
                      </Form.Label>

                      <Form.Select
                        name="lister_id"
                        value={
                          formData.lister_id
                        }
                        onChange={
                          handleInputChange
                        }
                        isInvalid={
                          !!errors.lister_id
                        }
                      >

                        <option value="">
                          Select Lister (Optional)
                        </option>

                        {listers.map(
                          (lister) => (
                            <option
                              key={lister.id}
                              value={lister.id}
                            >
                              {lister.name} (
                              {lister.email})
                            </option>
                          )
                        )}

                      </Form.Select>

                      <Form.Control.Feedback type="invalid">
                        {errors.lister_id &&
                          errors.lister_id[0]}
                      </Form.Control.Feedback>

                    </Form.Group>

                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Assign Marketplace User
                      </Form.Label>

                      <Form.Select
                        name="mplacer_id"
                        value={formData.mplacer_id}
                        onChange={handleInputChange}
                        isInvalid={!!errors.mplacer_id}
                      >
                        <option value="">
                          Select Marketplace User (Optional)
                        </option>

                        {mplacers.map((mplacer) => (
                          <option
                            key={mplacer.id}
                            value={mplacer.id}
                          >
                            {mplacer.name} ({mplacer.email})
                          </option>
                        ))}
                      </Form.Select>

                      <Form.Control.Feedback type="invalid">
                        {errors.mplacer_id &&
                          errors.mplacer_id[0]}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

              </div>

              {/* ===================================
                  PRICING
              ==================================== */}

              <div className="mc-card mb-4">

                <h4 className="card-title mb-4">
                  Pricing Configuration
                </h4>

                <Row>

                  {/* Buying Price */}

                  <Col md={6}>

                    <Form.Group className="mb-3">

                      <Form.Label>
                        Buying Price{" "}
                        <span className="text-danger">
                          *
                        </span>
                      </Form.Label>

                      <Form.Control
                        type="number"
                        step="0.01"
                        name="buying_price"
                        value={
                          formData.buying_price
                        }
                        onChange={
                          handleInputChange
                        }
                        isInvalid={
                          !!errors.buying_price
                        }
                        placeholder="0.00"
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors.buying_price &&
                          errors.buying_price[0]}
                      </Form.Control.Feedback>

                    </Form.Group>

                  </Col>

                  {/* DOLLAR PRICE */}

                  <Col md={6}>

                    <Form.Group className="mb-3">

                      <Form.Label>
                        Buying Price ($){" "}
                        <span className="text-danger">
                          *
                        </span>
                      </Form.Label>

                      <Form.Control
                        type="number"
                        step="0.01"
                        name="sell_price_dollar"
                        value={
                          formData.sell_price_dollar
                        }
                        onChange={
                          handleInputChange
                        }
                        isInvalid={
                          !!errors.sell_price_dollar
                        }
                        placeholder="0.00"
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors.sell_price_dollar &&
                          errors.sell_price_dollar[0]}
                      </Form.Control.Feedback>

                    </Form.Group>

                  </Col>

                  {/* SHIPPING */}

                  <Col md={6}>

                    <Form.Group className="mb-3">

                      <Form.Label>
                        Shipping Charges ($){" "}
                        <span className="text-danger">
                          *
                        </span>
                      </Form.Label>

                      <Form.Control
                        type="number"
                        step="0.01"
                        name="shipping_charges_dollar"
                        value={
                          formData.shipping_charges_dollar
                        }
                        onChange={
                          handleInputChange
                        }
                        isInvalid={
                          !!errors.shipping_charges_dollar
                        }
                        placeholder="0.00"
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors.shipping_charges_dollar &&
                          errors.shipping_charges_dollar[0]}
                      </Form.Control.Feedback>

                    </Form.Group>

                  </Col>

                  {/* BUYING PRICE */}

                  <Col md={6}>

                    <Form.Group className="mb-3">

                      <Form.Label>
                        Short Buying Price
                      </Form.Label>

                      <Form.Control
                        type="number"
                        step="0.01"
                        name="short_buying_price"
                        value={
                          formData.short_buying_price
                        }
                        onChange={
                          handleInputChange
                        }
                        isInvalid={
                          !!errors.short_buying_price
                        }
                        placeholder="0.00"
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors.short_buying_price &&
                          errors.short_buying_price[0]}
                      </Form.Control.Feedback>

                    </Form.Group>

                  </Col>

                </Row>

              </div>

            </Col>

            {/* =====================================
                RIGHT SIDE
            ====================================== */}

            <Col lg={4}>

              {/* ===================================
                  MAIN IMAGE
              ==================================== */}

              <div className="mc-card mb-4">

                <h4 className="card-title mb-4">
                  Main Product Image
                </h4>

                {/* FILE UPLOAD */}

                <Form.Group className="mb-3">

                  <Form.Label>
                    Product Image{" "}
                    <span className="text-danger">
                      *
                    </span>
                  </Form.Label>

                  <Form.Control
                    type="file"
                    accept="image/jpg,image/jpeg,image/png,image/webp"
                    onChange={
                      handleImageChange
                    }
                    isInvalid={
                      !!errors.first_img
                    }
                  />

                  <Form.Control.Feedback type="invalid">
                    {errors.first_img &&
                      errors.first_img[0]}
                  </Form.Control.Feedback>

                </Form.Group>

                {/* CAMERA BUTTON */}

                <div className="text-center mb-3">

                  <div className="text-muted mb-2">
                    OR
                  </div>

                  <Button
                    type="button"
                    variant="dark"
                    className="w-100"
                    onClick={
                      openCamera
                    }
                  >
                    📷 Take Photo
                  </Button>

                </div>

                {/* IMAGE PREVIEW */}

                {imgPreview && (

                  <div className="text-center mt-3">

                    <div
                      style={{
                        position:
                          "relative",
                        display:
                          "inline-block",
                        width: "100%",
                      }}
                    >

                      <img
                        src={
                          imgPreview
                        }
                        alt="Product Preview"
                        className="img-fluid rounded border"
                        style={{
                          width: "100%",
                          maxHeight:
                            "300px",
                          objectFit:
                            "contain",
                        }}
                      />

                      {/* REMOVE */}

                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        style={{
                          position:
                            "absolute",
                          top: "8px",
                          right: "8px",
                          borderRadius:
                            "50%",
                          width:
                            "32px",
                          height:
                            "32px",
                          padding: 0,
                          display:
                            "flex",
                          alignItems:
                            "center",
                          justifyContent:
                            "center",
                        }}
                        onClick={
                          removeImage
                        }
                      >
                        ×
                      </Button>

                    </div>

                  </div>

                )}

              </div>

              {/* ===================================
                  ACTIONS
              ==================================== */}

              <div className="mc-card">

                <Button
                  type="submit"
                  className="btn btn-primary w-100 mb-2"
                  disabled={
                    submitting
                  }
                >
                  {submitting
                    ? "Saving Product..."
                    : "Save Product"}
                </Button>

                <Button
                  type="button"
                  variant="secondary"
                  className="w-100"
                  onClick={() =>
                    navigate(
                      "/issue-product-list"
                    )
                  }
                  disabled={
                    submitting
                  }
                >
                  Cancel
                </Button>

              </div>

            </Col>

          </Row>

        </Form>

      </Container>

      {/* =========================================
          CAMERA MODAL
      ========================================== */}

      {cameraOpen && (

        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor:
              "rgba(0, 0, 0, 0.92)",
            zIndex: 99999,
            display: "flex",
            alignItems:
              "center",
            justifyContent:
              "center",
            padding: "20px",
          }}
        >

          <div
            style={{
              width: "100%",
              maxWidth: "700px",
              backgroundColor:
                "#000",
              borderRadius:
                "12px",
              overflow:
                "hidden",
            }}
          >

            {/* CAMERA HEADER */}

            <div
              style={{
                padding:
                  "15px",
                color: "#fff",
                display:
                  "flex",
                justifyContent:
                  "space-between",
                alignItems:
                  "center",
              }}
            >

              <h5
                style={{
                  margin: 0,
                }}
              >
                Take Product Photo
              </h5>

              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={
                  closeCamera
                }
              >
                Close
              </Button>

            </div>

            {/* CAMERA */}

            <div
              style={{
                width: "100%",
                background:
                  "#000",
                display:
                  "flex",
                justifyContent:
                  "center",
                alignItems:
                  "center",
              }}
            >

              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: "100%",
                  maxHeight:
                    "70vh",
                  objectFit:
                    "contain",
                  display:
                    "block",
                }}
              />

            </div>

            {/* CAMERA ACTIONS */}

            <div
              style={{
                padding:
                  "20px",
                display:
                  "flex",
                gap: "10px",
              }}
            >

              <Button
                type="button"
                variant="primary"
                className="w-100"
                onClick={
                  capturePhoto
                }
              >
                📸 Capture Photo
              </Button>

              <Button
                type="button"
                variant="secondary"
                className="w-100"
                onClick={
                  closeCamera
                }
              >
                Cancel
              </Button>

            </div>

          </div>

        </div>

      )}

    </PageLayout>
  );
}
